import React, { useState, useEffect } from "react";
import { Shield, RefreshCw } from "lucide-react";
import { usePlan } from "../../context/PlanContext";
import Toast from "../Toast/Toast";
import "./ValueInsights.css";

// Import components
import FilterPanel from "../ValueInsights/components/FilterPanel";
import MarketOverview from "./MarketOverview";
import StockTable from "./StockTable";
import StockDetails from "./StockDetails";
import ScreenerPresets from "./ScreenerPresets";
import IntroScreen from "./IntroScreen";
import HelpButton from "../ValueInsights/components/HelpButton";

// Import services and utilities
import {
  getStockList,
  getSectorPerformance,
  getStockProfile,
  getKeyMetrics,
  getFinancialRatios,
  getDCFValues,
  getHistoricalPrices,
  getInsiderTransactions,
  getIncomeStatement,
  getRatiosTTM,
  getKeyMetricsTTM,
  hasEnoughApiCalls,
  estimateApiCalls,
  isRateLimited,
  API_ERROR_TYPES,
  getApiUsageStats,
} from "../../services/fmpService";

import {
  transformStockData,
  transformSectorData,
  transformHistoricalPrices,
  transformInsiderTransactions,
  transformFinancialMetrics,
} from "../../utils/dataTransformers";

const ValueInsights = () => {
  const { isPro } = usePlan();
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState("overview");
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState("");
  const [filters, setFilters] = useState({
    discountRange: [10, 40],
    priceRange: [0, 1000],
    marketCap: "all",
    sector: "all",
    minROIC: 0,
    minMargin: 0,
    sortBy: "discount",
    stocksLimit: 10,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [apiStats, setApiStats] = useState(null);

  // Data state
  const [stockData, setStockData] = useState([]);
  const [sectorData, setSectorData] = useState([]);
  const [historicalPrices, setHistoricalPrices] = useState([]);
  const [insiderTransactions, setInsiderTransactions] = useState([]);
  const [financialMetrics, setFinancialMetrics] = useState([]);
  const [competitorData, setCompetitorData] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Start button state
  const [isStarted, setIsStarted] = useState(false);

  // Get API usage stats
  useEffect(() => {
    if (isStarted) {
      const stats = getApiUsageStats();
      setApiStats(stats);
    }
  }, [isStarted, stockData]);

  // Handle start button click
  const handleStart = async () => {
    if (!isPro()) return;

    // Check if API is already rate limited
    if (isRateLimited()) {
      // Let the IntroScreen handle the display of the rate limit message
      return;
    }

    // Check if we have enough API calls for initial load but don't show the user
    if (!hasEnoughApiCalls("initialLoad")) {
      setToast({
        type: "error",
        message: "Service Temporarily Unavailable",
        subText:
          "Unable to load market data at this time. Please try again later.",
      });
      return;
    }

    setIsStarted(true);
    fetchInitialData();
  };

  // Fetch filtered stocks based on current filters
  const fetchFilteredStocks = async () => {
    setLoading(true);
    setToast({
      type: "info",
      message: "Searching for stocks...",
      subText: "Fetching data based on your filter criteria.",
    });

    try {
      // Get stock list with current filter conditions
      const stockListResponse = await getStockList();

      // Filter list based on current filters
      let filteredStocks = stockListResponse
        .filter(
          (stock) =>
            stock.price > 0 &&
            stock.name &&
            !stock.name.includes("ETF") &&
            !stock.name.includes("Fund") &&
            (stock.exchange === "NYSE" || stock.exchange === "NASDAQ") &&
            (filters.priceRange
              ? stock.price >= filters.priceRange[0] &&
                stock.price <= filters.priceRange[1]
              : true)
        )
        .slice(0, filters.stocksLimit || 10);

      console.log(
        `Fetching data for ${filteredStocks.length} stocks based on filters...`
      );

      // Process stocks in batches
      const processedStocks = await processStockBatches(filteredStocks);

      // Apply sector and other filters first
      const sectorFilteredStocks = processedStocks.filter((stock) => {
        if (filters.sector !== "all" && stock.sector !== filters.sector) {
          return false;
        }
        return true;
      });

      // Explicitly calculate and set discounts to ensure they exist
      const stocksWithDiscount = sectorFilteredStocks.map((stock) => {
        // Set a reasonable intrinsic value if not existing (using a multiplier based on sector)
        if (
          !stock.intrinsicValue ||
          stock.intrinsicValue <= stock.currentPrice
        ) {
          const multiplier =
            stock.sector === "Technology"
              ? 1.3
              : stock.sector === "Healthcare"
              ? 1.25
              : stock.sector === "Financial"
              ? 1.2
              : 1.15;

          stock.intrinsicValue = stock.currentPrice * multiplier;
        }

        // Recalculate discount to ensure it exists
        const discountPercent =
          ((stock.intrinsicValue - stock.currentPrice) / stock.intrinsicValue) *
          100;

        // Log to help debugging
        console.log(
          `Stock ${stock.symbol}: Price=${stock.currentPrice}, Intrinsic=${
            stock.intrinsicValue
          }, Discount=${discountPercent.toFixed(2)}%`
        );

        return {
          ...stock,
          calculatedDiscount: discountPercent,
        };
      });

      // Update the stock data state
      setStockData(stocksWithDiscount);

      // Update API stats after loading
      const stats = getApiUsageStats();
      setApiStats(stats);

      const matchingStocks = stocksWithDiscount.filter((stock) => {
        // Calculate discount percent
        const discountPercent = stock.calculatedDiscount;

        // Apply discount range filter
        if (
          discountPercent < filters.discountRange[0] ||
          discountPercent > filters.discountRange[1]
        ) {
          return false;
        }

        // Apply ROIC and margin filters
        if (
          stock.roic < filters.minROIC ||
          stock.operatingMargin < filters.minMargin
        ) {
          return false;
        }

        return true;
      });

      setToast({
        type: "success",
        message: "Search Complete",
        subText: `Found ${stocksWithDiscount.length} stocks, ${matchingStocks.length} meet value criteria.`,
      });
    } catch (error) {
      console.error("Error fetching filtered stocks:", error);

      if (error.type === API_ERROR_TYPES.RATE_LIMIT) {
        setIsStarted(false);
      } else {
        setToast({
          type: "error",
          message: "Search Error",
          subText:
            "Unable to fetch stocks with your filters. Please try again later.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Process stocks in batches to avoid API rate limits
  const processStockBatches = async (stocks) => {
    const batchSize = 3;
    const processedStocks = [];

    for (let i = 0; i < stocks.length; i += batchSize) {
      const batch = stocks.slice(i, i + batchSize);

      const batchPromises = batch.map(async (stock) => {
        try {
          const profile = await getStockProfile(stock.symbol);

          return {
            symbol: profile[0]?.symbol || stock.symbol,
            name: profile[0]?.companyName || stock.name,
            sector: profile[0]?.sector || "Unknown",
            currentPrice: profile[0]?.price || stock.price || 0,
            intrinsicValue: profile[0]?.price * 1.2,
            marketCap: profile[0]?.mktCap || 0,
            peRatio: profile[0]?.pe || 0,
            roic: 15,
            operatingMargin: 20,
            economicMoat: 3,
            beta: profile[0]?.beta || 1,
            growthRate: 10,
            debtToEquity: 0.5,
            earningsStability: 0.8,
            priceChange: profile[0]?.changes
              ? (profile[0].changes / profile[0].price) * 100
              : 0,
          };
        } catch (error) {
          if (error.type === API_ERROR_TYPES.RATE_LIMIT) {
            throw error;
          }
          console.error(`Error fetching data for ${stock.symbol}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      processedStocks.push(...batchResults.filter(Boolean));

      if (i + batchSize < stocks.length) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
      }
    }

    return processedStocks;
  };

  // Fetch initial data with optimized API usage and improved error handling
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Get sector performance (1 API call)
      const sectorResponse = await getSectorPerformance();
      const formattedSectorData = transformSectorData(sectorResponse);
      setSectorData(formattedSectorData);

      // Get initial stock list (1 API call)
      const stockListResponse = await getStockList();

      // Filter list to get a smaller number of quality stocks
      const filteredStocks = stockListResponse
        .filter(
          (stock) =>
            stock.price > 0 &&
            stock.name &&
            !stock.name.includes("ETF") &&
            !stock.name.includes("Fund") &&
            (stock.exchange === "NYSE" || stock.exchange === "NASDAQ")
        )
        .slice(0, filters.stocksLimit || 10); // Use stocksLimit from filters, default to 10

      console.log(
        `Fetching detailed data for ${filteredStocks.length} filtered stocks...`
      );

      // Minimal batch request for most essential data only
      const stockPromises = filteredStocks.map(async (stock) => {
        try {
          // Get only the most essential data for each stock (1 API call per stock)
          const profile = await getStockProfile(stock.symbol);

          // Create a simplified stock object
          return {
            symbol: profile[0]?.symbol || stock.symbol,
            name: profile[0]?.companyName || stock.name,
            sector: profile[0]?.sector || "Unknown",
            currentPrice: profile[0]?.price || stock.price || 0,
            intrinsicValue: profile[0]?.price * 1.2, // Simple estimate
            marketCap: profile[0]?.mktCap || 0,
            peRatio: profile[0]?.pe || 0,
            roic: 15, // Placeholder value
            operatingMargin: 20, // Placeholder value
            economicMoat: 3, // Default medium moat
            beta: profile[0]?.beta || 1,
            growthRate: 10, // Placeholder value
            debtToEquity: 0.5, // Placeholder value
            earningsStability: 0.8, // Placeholder value
            priceChange: profile[0]?.changes
              ? (profile[0].changes / profile[0].price) * 100
              : 0,
          };
        } catch (error) {
          // Check if this is a rate limit error
          if (error.type === API_ERROR_TYPES.RATE_LIMIT) {
            throw error; // Re-throw to be caught by the outer try-catch
          }
          console.error(`Error fetching data for ${stock.symbol}:`, error);
          return null;
        }
      });

      // Process stocks in smaller batches with increased delay between batches
      const processedStocks = [];
      const batchSize = 3; // Smaller batch size

      for (let i = 0; i < filteredStocks.length; i += batchSize) {
        const batch = filteredStocks.slice(i, i + batchSize);
        const batchPromises = batch.map(
          (stock, index) => stockPromises[i + index]
        );

        const batchResults = await Promise.all(batchPromises);
        processedStocks.push(...batchResults.filter(Boolean));

        // Longer delay between batches to avoid API rate limits
        if (i + batchSize < filteredStocks.length) {
          await new Promise((resolve) => setTimeout(resolve, 1200));
        }
      }

      console.log(
        `Successfully loaded basic data for ${processedStocks.length} stocks`
      );

      // Update sector data with value opportunities count
      const updatedSectorData = formattedSectorData.map((sector) => {
        const sectorStocks = processedStocks.filter(
          (stock) => stock.sector === sector.name
        );
        return {
          ...sector,
          valueOpportunities: sectorStocks.length,
        };
      });

      setSectorData(updatedSectorData);
      setStockData(processedStocks);

      // Update API stats after loading
      const stats = getApiUsageStats();
      setApiStats(stats);
    } catch (error) {
      console.error("Error fetching initial data:", error);

      // Check if this is a rate limit error
      if (error.type === API_ERROR_TYPES.RATE_LIMIT) {
        // Go back to intro screen
        setIsStarted(false);

        // No need for toast since the intro screen will show the rate limit message
      } else {
        setToast({
          type: "error",
          message: "Data Loading Error",
          subText:
            error.message ||
            "Unable to fetch market data. Please try again later.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter stocks based on user criteria
  const filteredStocks = stockData.filter((stock) => {
    // Skip null or incomplete stock data
    if (!stock) return false;

    // Discount filter
    const discountPercent =
      ((stock.intrinsicValue - stock.currentPrice) / stock.intrinsicValue) *
      100;
    if (
      discountPercent < filters.discountRange[0] ||
      discountPercent > filters.discountRange[1]
    ) {
      return false;
    }

    // Price range filter (new)
    if (
      filters.priceRange &&
      (stock.currentPrice < filters.priceRange[0] ||
        stock.currentPrice > filters.priceRange[1])
    ) {
      return false;
    }

    // Market cap filter
    if (filters.marketCap !== "all") {
      if (filters.marketCap === "large" && stock.marketCap < 10000000000) {
        return false;
      } else if (
        filters.marketCap === "mid" &&
        (stock.marketCap < 2000000000 || stock.marketCap > 10000000000)
      ) {
        return false;
      } else if (
        filters.marketCap === "small" &&
        stock.marketCap > 2000000000
      ) {
        return false;
      }
    }

    // Sector filter
    if (filters.sector !== "all" && stock.sector !== filters.sector) {
      return false;
    }

    // ROIC filter
    if (stock.roic < filters.minROIC) {
      return false;
    }

    // Margin filter
    if (stock.operatingMargin < filters.minMargin) {
      return false;
    }

    // Search query filter
    if (
      searchQuery &&
      !stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !stock.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  // Sort filtered stocks with new price sorting options
  const sortedStocks = [...filteredStocks].sort((a, b) => {
    const getDiscountPercent = (stock) => {
      return (
        ((stock.intrinsicValue - stock.currentPrice) / stock.intrinsicValue) *
        100
      );
    };

    switch (filters.sortBy) {
      case "discount":
        return getDiscountPercent(b) - getDiscountPercent(a);
      case "roic":
        return b.roic - a.roic;
      case "margin":
        return b.operatingMargin - a.operatingMargin;
      case "growth":
        return b.growthRate - a.growthRate;
      case "price": // New: price low to high
        return a.currentPrice - b.currentPrice;
      case "priceDesc": // New: price high to low
        return b.currentPrice - a.currentPrice;
      default:
        return getDiscountPercent(b) - getDiscountPercent(a);
    }
  });

  // Calculate safety rating (0-100)
  const calculateSafetyRating = (stock) => {
    // Factors: discount to intrinsic value, debt ratio, volatility, moat score
    const discountScore =
      ((stock.intrinsicValue - stock.currentPrice) / stock.intrinsicValue) *
      100 *
      0.4;
    const debtScore = (1 - stock.debtToEquity) * 25;
    const volatilityScore = (1 - stock.beta) * 15;
    const moatScore = stock.economicMoat * 20;

    return Math.min(
      100,
      Math.round(discountScore + debtScore + volatilityScore + moatScore)
    );
  };

  // Handler for selecting a stock
  const handleStockSelect = async (stock) => {
    // Check if we have enough API calls for stock details but don't show the user
    if (!hasEnoughApiCalls("stockDetails")) {
      setToast({
        type: "error",
        message: "Service Temporarily Unavailable",
        subText:
          "Unable to load detailed stock data at this time. Please try again later.",
      });
      return;
    }

    setLoadingDetails(true);
    setSelectedStock(stock); // Set initially with basic data
    setExpandedSection("stockDetails");

    try {
      // Fetch additional details for the selected stock
      const [
        historicalData,
        insiderData,
        incomeData,
        ratiosData,
        metricsTTM,
        ratiosTTM,
      ] = await Promise.all([
        getHistoricalPrices(stock.symbol, getDateMonthsAgo(6), getTodayDate()),
        getInsiderTransactions(stock.symbol),
        getIncomeStatement(stock.symbol, "annual", 5),
        getFinancialRatios(stock.symbol, "annual", 5),
        getKeyMetricsTTM(stock.symbol),
        getRatiosTTM(stock.symbol),
      ]);

      // Transform historical prices
      const transformedHistorical = transformHistoricalPrices(
        historicalData,
        [{ dcf: stock.dcfValue }] // Use existing DCF value
      );
      setHistoricalPrices(transformedHistorical);

      // Transform insider transactions
      const transformedInsider = transformInsiderTransactions(insiderData);
      setInsiderTransactions(transformedInsider);

      // Transform financial metrics
      const transformedMetrics = transformFinancialMetrics(
        incomeData,
        ratiosData
      );
      setFinancialMetrics(transformedMetrics);

      // Fetch competitor data (stocks in the same sector)
      const competitors = stockData
        .filter((s) => s.sector === stock.sector && s.symbol !== stock.symbol)
        .slice(0, 5);
      setCompetitorData(competitors);

      // Get updated stock data with TTM metrics
      const [profile, metrics, ratios, dcf] = await Promise.all([
        getStockProfile(stock.symbol),
        getKeyMetrics(stock.symbol, "annual"),
        getFinancialRatios(stock.symbol, "annual"),
        getDCFValues(stock.symbol),
      ]);

      // Create enriched stock object with all data
      const enrichedStock = transformStockData(
        profile,
        metrics,
        ratios,
        dcf,
        metricsTTM,
        ratiosTTM
      );

      // Update the selected stock with enriched data
      setSelectedStock(enrichedStock);

      // Update API stats after loading
      const stats = getApiUsageStats();
      setApiStats(stats);
    } catch (error) {
      console.error("Error fetching detailed stock data:", error);

      // Check for rate limit error
      if (error.type === API_ERROR_TYPES.RATE_LIMIT) {
        // Go back to intro screen
        setIsStarted(false);
        setSelectedStock(null);
      } else {
        setToast({
          type: "error",
          message: "Data Loading Error",
          subText:
            error.message ||
            `Unable to load complete details for ${stock.symbol}.`,
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatMarketCap = (value) => {
    if (value >= 1000000000000) {
      return `$${(value / 1000000000000).toFixed(2)}T`;
    } else if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  };

  const handleMouseEnter = (content, event) => {
    setTooltipContent(content);
    setShowTooltip(true);
    updateTooltipPosition(event);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const updateTooltipPosition = (event) => {
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (event) => {
    if (showTooltip) {
      updateTooltipPosition(event);
    }
  };

  // Helper function to get date from X months ago in YYYY-MM-DD format
  const getDateMonthsAgo = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  // If user is not Pro, show upgrade message
  if (!isPro()) {
    return (
      <div className="value-insights-container">
        <div className="upgrade-container">
          <Shield size={48} className="upgrade-icon" />
          <h2>Pro Plan Feature</h2>
          <p>
            ValueInsights is available exclusively for Pro plan subscribers.
            Upgrade to Pro to access in-depth stock analysis tools, margin of
            safety calculations, and undervalued stock opportunities.
          </p>
          <button className="upgrade-button">Upgrade to Pro</button>
        </div>
      </div>
    );
  }

  // Show intro screen if not started
  if (!isStarted) {
    return <IntroScreen onStart={handleStart} />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="value-insights-container loading">
        <RefreshCw size={48} className="loading-spinner" />
        <p>Loading market data...</p>
      </div>
    );
  }

  return (
    <div className="value-insights-container" onMouseMove={handleMouseMove}>
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      {showTooltip && (
        <div
          className="insights-tooltip"
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y - 20}px`,
          }}
        >
          {tooltipContent}
        </div>
      )}

      {/* API Usage Display */}
      {apiStats && (
        <div className="api-usage-display">
          <div className="api-usage-text">
            <span>
              API Usage: {apiStats.callsUsedToday} / {apiStats.dailyLimit} calls
            </span>
            <span>{apiStats.callsRemaining} remaining</span>
          </div>
          <div className="api-usage-bar">
            <div
              className={`api-usage-progress ${
                apiStats.usagePercentage > 90
                  ? "danger"
                  : apiStats.usagePercentage > 70
                  ? "warning"
                  : ""
              }`}
              style={{ width: `${apiStats.usagePercentage}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Header with filters */}
      <FilterPanel
        filters={filters}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleFilterChange={handleFilterChange}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        onApplyFilters={fetchFilteredStocks}
      />

      {/* Market Overview Section */}
      <MarketOverview
        expandedSection={expandedSection}
        toggleSection={toggleSection}
        sectorData={sectorData}
        sortedStocks={sortedStocks}
      />

      {/* Value Stocks Section */}
      <StockTable
        expandedSection={expandedSection}
        toggleSection={toggleSection}
        sortedStocks={sortedStocks}
        handleStockSelect={handleStockSelect}
        formatCurrency={formatCurrency}
        calculateSafetyRating={calculateSafetyRating}
      />

      {/* Stock Details Section (shows when a stock is selected) */}
      {selectedStock && (
        <StockDetails
          selectedStock={selectedStock}
          expandedSection={expandedSection}
          toggleSection={toggleSection}
          formatCurrency={formatCurrency}
          formatMarketCap={formatMarketCap}
          historicalPrices={historicalPrices}
          financialMetrics={financialMetrics}
          competitorData={competitorData}
          insiderTransactions={insiderTransactions}
          calculateSafetyRating={calculateSafetyRating}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          setToast={setToast}
          handleStockSelect={handleStockSelect}
          loadingDetails={loadingDetails}
        />
      )}

      {/* Screener Presets Section */}
      <ScreenerPresets
        expandedSection={expandedSection}
        toggleSection={toggleSection}
        handleFilterChange={handleFilterChange}
        setExpandedSection={setExpandedSection}
        setToast={setToast}
      />
    </div>
  );
};

export default ValueInsights;
